import { useEffect, useState } from "react";
import { flashMood } from "../lib/mood.ts";
import { Icon } from "./Icon.tsx";
import ui from "./ui.module.css";

type Props = {
  value: string;
  label?: string;
  /** What the live region says after a copy. */
  copied?: string;
  compact?: boolean;
  className?: string;
  variant?: "frame" | "ghost";
};

export function CopyButton({
  value,
  label = "Copy link",
  copied = "Link copied",
  compact = false,
  className,
  variant,
}: Props) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");

  useEffect(() => {
    if (state === "idle") return;
    const timer = setTimeout(() => setState("idle"), 2000);
    return () => clearTimeout(timer);
  }, [state]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setState("copied");
      flashMood("wink", 900);
    } catch {
      setState("failed");
    }
  };

  const text = state === "copied" ? "Copied" : state === "failed" ? "Copy failed" : label;
  const idle = variant ?? (compact ? "ghost" : undefined);
  const shown = state === "failed" ? "outline-danger" : state === "idle" ? idle : undefined;

  return (
    <>
      <button
        type="button"
        className={`${compact ? ui.iconButton : ui.button} ${className ?? ""}`}
        data-variant={shown}
        data-state={state}
        onClick={copy}
        aria-label={compact ? text : undefined}
        title={compact ? text : undefined}
      >
        <Icon name={state === "copied" ? "check" : state === "failed" ? "alert" : "copy"} />
        {compact ? null : <span>{text}</span>}
      </button>
      <span className={ui.visuallyHidden} aria-live="polite">
        {state === "copied" ? copied : state === "failed" ? "Copy failed" : ""}
      </span>
    </>
  );
}
